import {
  BookingApiResponse,
  SelectedBooking,
} from "../../api/bookings/IBookings";
import {
  getAllSelectableBookingsAsync,
  getBookingsAsync,
  getBookingsByIdAsync,
  updateBookingAsync,
} from "../../api/bookings/bookings";
import { BookingSentInfo, BookingsState } from "../types/bookingTypes";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  ActionReducerMapBuilder,
  AsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import RootState from "../types/rootState";
import { BookingValidationErrorFromAPI, CustomError } from "../../api/ApiError";
import { convertSearchFilterToFilterRequest } from "../../utils/converters";
import { sendBookingAsync } from "../../api/bookings/send/send";
import { ISelectedTerminal } from "../../api/terminal/ITerminal";
import { IUpdateBooking } from "../../api/bookings/IUpdate";
import { toast } from "react-toastify";

const initialState: BookingsState = {
  bookingsResponse: null,
  selectedBookings: [],
  selectedBookingsFromApi: [],
  isLoading: false,
  error: undefined,
  isSelectableBookingLoading: false, // New loading state
  bookingSentInfo: undefined,
  currentPage: 1,
};

export const getSelectedBookings = createAsyncThunk(
  "bookings/getSelectedBookings",
  async (nonValidateBookings: SelectedBooking[] | undefined, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const filterData = state.searchFilter;
    const apiFilterRequest = convertSearchFilterToFilterRequest(filterData);
    const bookingSelectedApiResponse = await getAllSelectableBookingsAsync(
      apiFilterRequest,
      nonValidateBookings
    ).catch((error: CustomError) => {
      return thunkApi.rejectWithValue(error);
    });
    return bookingSelectedApiResponse;
  }
);

export const fetchBookings = createAsyncThunk(
  "bookings/fetchBookings",
  async (
    {
      page,
      triggerGetSelectedBookings = true,
    }: { page: number; triggerGetSelectedBookings?: boolean },
    thunkApi
  ) => {
    const state = thunkApi.getState() as RootState;
    const filterData = state.searchFilter;
    const apiFilterRequest = convertSearchFilterToFilterRequest(filterData);
    return await getBookingsAsync(page, 50, apiFilterRequest)
      .catch((error: CustomError) => {
        return thunkApi.rejectWithValue(error);
      })
      .finally(() => {
        if (triggerGetSelectedBookings) {
          thunkApi.dispatch(getSelectedBookings());
        }
      });
  }
);

export const getBookingsById = createAsyncThunk(
  "bookings/getBookingsById",
  async (nonValidateBookings: SelectedBooking[], thunkApi) => {
    return await getBookingsByIdAsync(nonValidateBookings)
      .catch((error: CustomError) => {
        return thunkApi.rejectWithValue(error);
      })
      .finally(() => {
        thunkApi.dispatch(getSelectedBookings(nonValidateBookings));
      });
  }
);

export interface IUpdateBookingWithBookingId {
  bookingId: number;
  updateBooking: IUpdateBooking;
  userName: string;
}

export const updateBooking = createAsyncThunk(
  "bookings/updateBooking",
  async (data: IUpdateBookingWithBookingId, thunkApi) => {
    const { bookingId, updateBooking, userName } = data;
    return await updateBookingAsync(updateBooking, bookingId, userName)
      .then(() => {
        const state = thunkApi.getState() as RootState;
        const sentBookingInfo = state.bookings.bookingSentInfo;
        if (sentBookingInfo !== undefined)
          thunkApi.dispatch(
            getBookingsById(sentBookingInfo.nonValidateBookings)
          );
        else
          thunkApi
            .dispatch(fetchBookings({ page: state.bookings.currentPage }))
            .unwrap();
      })
      .catch((error: CustomError) => {
        return thunkApi.rejectWithValue(error);
      });
  }
);

export const sendBooking = createAsyncThunk(
  "bookings/sendBooking",
  async (terminals: ISelectedTerminal, thunkApi) => {
    const state = thunkApi.getState() as RootState;

    const selectedBookings = state.bookings.selectedBookings;

    return await sendBookingAsync(selectedBookings, terminals)
      .then((nonValidatedBookings) => {
        if (nonValidatedBookings.length > 0) {
          const submittedBookingsCount =
            selectedBookings.length - nonValidatedBookings.length;
          const missingBookingsCount = nonValidatedBookings.length;
          thunkApi.dispatch(
            setSentBookingInfo({
              submittedBookingsCount: submittedBookingsCount,
              missingBookingsCount: missingBookingsCount,
              nonValidateBookings: nonValidatedBookings,
            })
          );

          toast.success(`Submitted bookings: ${submittedBookingsCount}`);
          toast.error(`Missing bookings: ${missingBookingsCount}`);

          thunkApi.dispatch(getBookingsById(nonValidatedBookings)).unwrap();
        } else {
          thunkApi.dispatch(resetSentBookingInfo());
          thunkApi
            .dispatch(fetchBookings({ page: state.bookings.currentPage }))
            .unwrap();
        }
      })
      .catch((error: CustomError) => {
        return thunkApi.rejectWithValue(error);
      })
      .finally(() => {
        thunkApi.dispatch(setSelectedBooking([]));
      });
  }
);

const handleAsyncThunk = <Returned, ThunkArg>(
  builder: ActionReducerMapBuilder<BookingsState>,
  thunk: AsyncThunk<Returned, ThunkArg, object>,
  process?: (
    state: BookingsState,
    action: ReturnType<typeof thunk.fulfilled>
  ) => void,
  loadingKey: keyof BookingsState = "isLoading" // Default loading key
) => {
  builder.addCase(thunk.pending, (state) => {
    (state[loadingKey] as boolean) = true;
    state.error = undefined;
  });
  builder.addCase(thunk.fulfilled, (state, action) => {
    (state[loadingKey] as boolean) = false;
    state.error = undefined;
    if (process) process(state, action);
  });
  builder.addCase(thunk.rejected, (state, action) => {
    (state[loadingKey] as boolean) = false;
    state.error = action.payload as CustomError;
  });
};

export const bookingSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    setSelectedBooking(state, action: PayloadAction<SelectedBooking[]>) {
      state.selectedBookings = action.payload;
    },
    setError(state, action: PayloadAction<BookingValidationErrorFromAPI>) {
      state.error = action.payload;
    },
    setSendBookingPopupOpen(state, action: PayloadAction<boolean>) {
      state.shouldBookingValidationPopupOpen = action.payload;
    },
    setSentBookingInfo(state, action: PayloadAction<BookingSentInfo>) {
      state.bookingSentInfo = action.payload;
    },
    resetSentBookingInfo(state) {
      state.bookingSentInfo = undefined;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    handleAsyncThunk(builder, fetchBookings, (state, action) => {
      state.bookingsResponse = action.payload as BookingApiResponse;
    });

    handleAsyncThunk(builder, getBookingsById, (state, action) => {
      state.bookingsResponse = action.payload as BookingApiResponse;
      state.selectedBookings = [];
    });

    handleAsyncThunk(
      builder,
      getSelectedBookings,
      (state, action) => {
        state.selectedBookingsFromApi = action.payload as SelectedBooking[];
      },
      "isSelectableBookingLoading"
    );

    handleAsyncThunk(builder, updateBooking);

    handleAsyncThunk(builder, sendBooking, (state) => {
      state.shouldBookingValidationPopupOpen = true;
    });
  },
});

export const {
  setCurrentPage,
  setSelectedBooking,
  setError,
  setSendBookingPopupOpen,
  setSentBookingInfo,
  resetSentBookingInfo,
} = bookingSlice.actions;
export default bookingSlice.reducer;
